import React, { useEffect, useState } from "react";
import BasicInfoTab from "./local-info-tabs/DefaultInfoTab";
import ContactInfoTab from "./local-info-tabs/ContactInfoTab";
import PhotosInfoTab from "./local-info-tabs/PhotosInfoTab";

import { DisketteSaveIcon } from "../../../assets/icons";
import { UpdateLocalBasicInfoDTO, UpdateLocalContactInfoDTO, UpdateLocalImagesInfoDTO } from "../../../models/local/local.request";
import { SocialLinkType } from "../../../models/base/SocialLinkType";
import { useNotification } from "../../toster-notification/NotificationContext";
import { localService } from "../../../services/localService";
import { useParams } from "react-router-dom";
import { LocalSettingsInfoDTO, SocialLinkDTO } from "../../../models/local/local.response";
import Loader from "../../shared/Loader";
import { ImageList } from "@mui/material";
import DefaultSettings from "./local-info-tabs/DefaultSettings";
import { LocalSettingsDTO } from "../../../models/localSettings/localSettings.request";
import { localSettingsService } from "../../../services/localSettingsService";

const InfoPage: React.FC = () => {

    const [selectedTab, setSelectedTab] = React.useState<string>("basic-info");

    const [basicInfo, setBasicInfo] = React.useState<UpdateLocalBasicInfoDTO>({
        name: "",
        description: "",
        workingHours: "",
        wifiName: "",
        wifiPassword: ""
    });

    const [contactInfo, setContactInfo] = React.useState<UpdateLocalContactInfoDTO>({
        email: "",
        address: "",
        phoneNumber: "",
        website: "",
        longitude: "",
        latitude: "",
        socialLinks: {
            [SocialLinkType.YouTube]: { name: "", url: "" },
            [SocialLinkType.Facebook]: { name: "", url: "" },
            [SocialLinkType.Instagram]: { name: "", url: "" },
            [SocialLinkType.X]: { name: "", url: "" },
            [SocialLinkType.TikTok]: { name: "", url: "" },
            [SocialLinkType.LinkedIn]: { name: "", url: "" }
        }
    });

    const [photosInfo, setPhotosInfo] = React.useState<UpdateLocalImagesInfoDTO>({
        logoImageUrl: "",
        backgroundImageUrl: "",
        logoFileImage: null,
        backgroundFileImage: null
    });

    const [defaultSettings, setDefaultSettings] = useState<LocalSettingsDTO>({
        showAddToCartButton: true,
        dineInLayoutImageUrl: ""
    });

    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotification();
    const { localId } = useParams<{ localId: string }>();

    useEffect(() => {
        fetchSettingsInfo();
        fetchDefaultSettings();
    }, [localId]);


    const fetchDefaultSettings = async () => {
        if (!localId) return;

        setLoading(true);
        try {
            const response = await localSettingsService.getLocalSettings(localId);
            if (response.success && response.data) {
                setDefaultSettings(response.data);
            } else {
                notify("error", "Greška pri učitavanju podataka o podešavanjima lokala.");
            }
        } catch (error) {
            notify("error", "Greška pri učitavanju podataka o podešavanjima lokala.");
        } finally {
            setLoading(false);
        }
    };

    const fetchSettingsInfo = async () => {
        if (!localId) return;

        setLoading(true);
        try {
            const response = await localService.getSettingsInfo(localId)

            if (response.success && response.data) {
                const data: LocalSettingsInfoDTO = response.data;
                setBasicInfo({
                    name: data.name,
                    description: data.description,
                    workingHours: data.workingHours,
                    wifiName: data.wiFiName,
                    wifiPassword: data.wiFiPassword
                });

                setContactInfo({
                    latitude: data.latitude,
                    longitude: data.longitude,
                    email: data.email,
                    address: data.address,
                    phoneNumber: data.phoneNumber,
                    website: data.website,
                    socialLinks: mapSocialLinksFromServer(data.socialLinks as any)
                });

                setPhotosInfo({
                    logoImageUrl: data.logoImageUrl,
                    backgroundImageUrl: data.backgroundImageUrl,
                    logoFileImage: null,
                    backgroundFileImage: null
                });

            } else {
                notify("error", "Greška pri učitavanju podataka o podešavanjima lokala.");
            }
        } catch (error) {
            notify("error", "Greška pri učitavanju podataka o podešavanjima lokala.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        setLoading(true);

        await handleUpdateBasicInfo();
        await handleUpdateContantInfo();
        await handleUpdateImagesInfo();
        await handleUpdateDefaultSettings();

        setLoading(false);
    }

    const handleUpdateContantInfo = async () => {
        if (localId == null)
            return;

        setLoading(true);

        try {

            const response = await localService.updateContactInfo(localId, contactInfo);

            if (response.success) {
                notify("success", "Uspesno sačuvana kontakt podešavanja")
            } else {
                notify("error", "Greška prilikom čuvanja kontakt podešavanja")
            }
        } catch (error: any) {
            notify("error", "Greška prilikom čuvanja kontakt podešavanja");
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            }
        } finally {
            setLoading(false);
        }
    };


    const handleUpdateBasicInfo = async () => {
        if (localId == null)
            return;

        setLoading(true);

        try {

            const response = await localService.updateBasicInfo(localId, basicInfo);

            if (response.success) {
                notify("success", "Uspesno sačuvana podešavanja osnovnih informacija")
            } else {
                notify("error", "Greška prilikom čuvanja podešavanja osnovnih informacija")
            }
        } catch (error: any) {
            notify("error", "Greška prilikom čuvanja podešavanja osnovnih informacija")
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateImagesInfo = async () => {
        if (localId == null)
            return;

        setLoading(true);

        try {

            const form = new FormData();

            form.append("logoImageUrl", photosInfo.logoImageUrl);
            form.append("backgroundImageUrl", photosInfo.backgroundImageUrl);

            if (photosInfo.logoFileImage) {
                form.append("logoFileImage", photosInfo.logoFileImage);
            }

            if (photosInfo.backgroundFileImage) {
                form.append("backgroundFileImage", photosInfo.backgroundFileImage);
            }

            const response = await localService.updateImagesInfo(localId, form);

            if (response.success) {
                notify("success", "Uspesno sačuvana podešavanja slika")
            } else {
                notify("error", "Greška prilikom čuvanja podešavanja slika")
            }
        } catch (error: any) {
            notify("error", "Greška prilikom čuvanja podešavanja slika")
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateDefaultSettings = async () => {
        if (localId == null)
            return;

        setLoading(true);

        try {
            const response = await localSettingsService.updateLocalSettings(localId, defaultSettings);
            if (response.success) {
                notify("success", "Uspesno sačuvana podešavanja lokala")
            } else {
                notify("error", "Greška prilikom čuvanja podešavanja lokala")
            }
        } catch (error: any) {
            notify("error", "Greška prilikom čuvanja podešavanja lokala")
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const mapSocialLinksFromServer = (
        links: Record<string, SocialLinkDTO>
    ): Record<SocialLinkType, SocialLinkDTO> => {
        return {
            [SocialLinkType.Facebook]: links.Facebook ?? { name: "", url: "" },
            [SocialLinkType.Instagram]: links.Instagram ?? { name: "", url: "" },
            [SocialLinkType.X]: links.X ?? { name: "", url: "" },
            [SocialLinkType.LinkedIn]: links.LinkedIn ?? { name: "", url: "" },
            [SocialLinkType.TikTok]: links.TikTok ?? { name: "", url: "" },
            [SocialLinkType.YouTube]: links.YouTube ?? { name: "", url: "" },
        };
    };

    if (loading) {
        return <Loader />
    }

    return (
        <section className="info-section">
            <div className="w-layout-blockcontainer container w-container">
                <div className="sttings-content-wrapper">
                    <div className="header-div">
                        <div>
                            <h2 className="heading-3">Podešavanja lokala</h2>
                            <div className="text-block">
                                Uredite informacije o vašem lokalu<br />
                            </div>
                        </div>
                        <div className="header-button-div">
                            <div className="button-primary" onClick={() => handleUpdate()}>
                                <img loading="lazy" src={DisketteSaveIcon} alt="" className="button-image" />
                                <div className="button-text">Sačuvaj podešavanja</div>
                            </div>
                        </div>
                    </div>
                    <div className="info-setting-block">
                        <div className="tab-list-div">
                            <div className={`tab-div ${selectedTab === "basic-info" ? "tab-div-select" : ""}`} onClick={() => setSelectedTab("basic-info")}>
                                <div className="tab-text">Osnovne informacije</div>
                            </div>
                            <div className={`tab-div ${selectedTab === "contact-info" ? "tab-div-select" : ""}`} onClick={() => setSelectedTab("contact-info")}>
                                <div className="tab-text">Kontakt</div>
                            </div>
                            <div className={`tab-div ${selectedTab === "photos-info" ? "tab-div-select" : ""}`} onClick={() => setSelectedTab("photos-info")}>
                                <div className="tab-text">Slike</div>
                            </div>
                            <div className={`tab-div ${selectedTab === "default-info" ? "tab-div-select" : ""}`} onClick={() => setSelectedTab("default-info")}>
                                <div className="tab-text">Opšta podešavanja</div>
                            </div>
                        </div>

                        <div className="tab-view">

                            {selectedTab === "basic-info" && (
                                <BasicInfoTab
                                    value={basicInfo}
                                    onChange={setBasicInfo}
                                />
                            )}

                            {selectedTab === "contact-info" && (
                                <ContactInfoTab
                                    value={contactInfo}
                                    onChange={setContactInfo}
                                />
                            )}

                            {selectedTab === "photos-info" && (
                                <PhotosInfoTab
                                    value={photosInfo}
                                    onChange={setPhotosInfo}
                                />
                            )}

                            {selectedTab === "default-info" && (
                                <DefaultSettings
                                    defaultSettings={defaultSettings}
                                    onSettingsChange={setDefaultSettings}
                                />
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default InfoPage;